import { Ops } from '@willie/schemas'
import type { TRPCOutputs } from '~/utils/trpc'
import { localDayKey } from '~/utils/date'
import { STAGE_OPTIONS, type Stage } from './constants'
import { computeStage, emptyByStage, isRetired, needsCompatibilityCheck, needsVerification } from './service'

type Installation = TRPCOutputs['ops']['stationInstallations']['list'][number]

const STUCK_AMBER_DAYS = 14
const STUCK_RED_DAYS = 30
export const INDEX_ALIGNMENT_DELAY_DAYS = 30
export const FIRST_TRANSMISSION_DELAY_HOURS = 48
export const ACTIVITY_TREND_WEEKS = 12

export const daysStalledColor = (days: number): 'red' | 'amber' | undefined => {
  if (days > STUCK_RED_DAYS) return 'red'
  if (days > STUCK_AMBER_DAYS) return 'amber'
  return undefined
}

// Whole days elapsed since an ISO timestamp, floored and never negative.
export const integerDaysSince = (isoString: string): number =>
  Math.max(0, Math.floor(daysSince(isoString, new Date()) ?? 0))

export interface OverviewKPIs {
  totalInstallations: number
  retiredCount: number
  activeOrgs: number
  installedCount: number
  installedPercent: number
  indexAlignedCount: number
  indexAlignedPercent: number
}

export interface StagePipelineEntry {
  stage: Stage
  count: number
}

export interface AttentionBuckets {
  indexAlignmentNeeded: number
  verificationNeeded: number
  awaitingFirstTransmission: number
}

export interface CompatibilityQueue {
  toCheck: number
  notCompatibleUnretired: number
}

export interface OverviewData {
  kpis: OverviewKPIs
  pipeline: StagePipelineEntry[]
  attention: AttentionBuckets
  compatibility: CompatibilityQueue
  indexAlignmentNeededIds: number[]
  awaitingFirstTransmissionIds: number[]
}

export const daysSince = (dateStr: string | null, now: Date): number | null => {
  if (!dateStr) return null
  const t = new Date(dateStr).getTime()
  if (Number.isNaN(t)) return null
  return (now.getTime() - t) / (1000 * 60 * 60 * 24)
}

const computeCompatibilityQueue = (installations: Installation[]): CompatibilityQueue => ({
  toCheck: installations.filter(needsCompatibilityCheck).length,
  notCompatibleUnretired: installations.filter((row) => row.compatibility_status === 'not_compatible').length
})

/**
 * Every figure but the retired count describes work still in play: an installation nobody is
 * coming back for would otherwise drag down installedPercent and keep appearing in the buckets
 * that prompt someone to act.
 */
export const computeOverview = (
  allInstallations: Installation[],
  now: Date = new Date()
): OverviewData => {
  const installations = allInstallations.filter((row) => !isRetired(row))
  const retiredCount = allInstallations.length - installations.length
  const stageCounts = emptyByStage()
  const orgIdsWithInstallations = new Set<string>()

  let installedCount = 0
  let indexAlignmentNeeded = 0
  let verificationNeeded = 0
  let awaitingFirstTransmission = 0
  const indexAlignmentNeededIds: number[] = []
  const awaitingFirstTransmissionIds: number[] = []

  for (const row of installations) {
    const stage = computeStage(row)
    stageCounts[stage] += 1

    const installAge = daysSince(row.installation_date, now)
    const isStalled = row.current_stall !== null

    if (stage === 'station_installed' || stage === 'index_aligned') installedCount += 1
    // Counted regardless of an open stall: a submission is evidence the visit happened.
    if (needsVerification(row)) verificationNeeded += 1
    if (!isStalled && stage === 'station_installed' && installAge !== null && installAge >= INDEX_ALIGNMENT_DELAY_DAYS) {
      indexAlignmentNeeded += 1
      indexAlignmentNeededIds.push(row.installation_id)
    }
    if (
      row.first_transmission_at === null &&
      installAge !== null &&
      installAge * 24 > FIRST_TRANSMISSION_DELAY_HOURS &&
      installAge < Ops.StationInstallations.FIRST_TRANSMISSION_WINDOW_DAYS
    ) {
      awaitingFirstTransmission += 1
      awaitingFirstTransmissionIds.push(row.installation_id)
    }
    if (row.org_id) orgIdsWithInstallations.add(row.org_id)
  }

  const total = installations.length

  return {
    kpis: {
      totalInstallations: total,
      retiredCount,
      activeOrgs: orgIdsWithInstallations.size,
      installedCount,
      installedPercent: total === 0 ? 0 : Math.round((installedCount / total) * 100),
      indexAlignedCount: stageCounts.index_aligned,
      indexAlignedPercent: total === 0 ? 0 : Math.round((stageCounts.index_aligned / total) * 100)
    },
    pipeline: STAGE_OPTIONS.map((stage) => ({ stage, count: stageCounts[stage] })),
    attention: {
      indexAlignmentNeeded,
      verificationNeeded,
      awaitingFirstTransmission
    },
    compatibility: computeCompatibilityQueue(installations),
    indexAlignmentNeededIds,
    awaitingFirstTransmissionIds
  }
}

export const ACTIVITY_METRICS = [
  'newInstallations',
  'ordersPlaced',
  'loggersShipped',
  'installationsDone',
  'indexAligned'
] as const
export type ActivityMetric = (typeof ACTIVITY_METRICS)[number]

export const activityMetricLabel: Record<ActivityMetric, string> = {
  newInstallations: 'New installations',
  ordersPlaced: 'Orders placed',
  loggersShipped: 'Loggers shipped',
  installationsDone: 'Installations done',
  indexAligned: 'Index aligned'
}

export type ActivityTrendPoint = Record<ActivityMetric, number> & {
  date: string
  isPartial: boolean
}

const activityMetricDate = (row: Installation): Record<ActivityMetric, string | null> => ({
  newInstallations: row.created_at,
  ordersPlaced: row.order_placed_date,
  loggersShipped: row.logger_shipping_date,
  installationsDone: row.installation_date,
  indexAligned: row.index_aligned_date
})

// Monday 00:00 (local) of the calendar week containing `date`. getDay() is Sun=0..Sat=6;
// treat Sunday as 7 so Monday is the week start. Day arithmetic via the Date constructor
// (rather than millisecond math) normalizes to local midnight, keeping week boundaries and
// labels correct across DST transitions.
const startOfWeek = (date: Date): Date => {
  const isoDay = date.getDay() === 0 ? 7 : date.getDay()
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() - (isoDay - 1))
}

// `date` columns hold a calendar day: new Date('2026-10-26') is UTC midnight, still Sunday west of UTC.
const activityDay = (value: string): string | null => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value
  const at = new Date(value)
  return Number.isNaN(at.getTime()) ? null : localDayKey(at)
}

const weekLabel = (weekStart: Date): string =>
  weekStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })

// Fixed Mon–Sun calendar-week buckets, oldest → current; boundaries don't shift with the
// time of load, so bars are comparable week over week. The current week is still in progress
// (isPartial) — its bar reflects only elapsed days.
export const computeActivityTrend = (
  installations: Installation[],
  now: Date = new Date()
): ActivityTrendPoint[] => {
  const base = startOfWeek(now)
  return Array.from({ length: ACTIVITY_TREND_WEEKS }, (_, i) => {
    const weeksAgo = ACTIVITY_TREND_WEEKS - 1 - i
    const weekStart = new Date(base.getFullYear(), base.getMonth(), base.getDate() - weeksAgo * 7)
    const weekStartDay = localDayKey(weekStart)
    const weekEndDay = localDayKey(new Date(base.getFullYear(), base.getMonth(), base.getDate() - weeksAgo * 7 + 7))
    const counts = Object.fromEntries(
      ACTIVITY_METRICS.map((metric) => [
        metric,
        installations.filter((row) => {
          const dateStr = activityMetricDate(row)[metric]
          const day = dateStr ? activityDay(dateStr) : null
          return day !== null && day >= weekStartDay && day < weekEndDay
        }).length
      ])
    ) as Record<ActivityMetric, number>
    return { ...counts, date: weekLabel(weekStart), isPartial: weeksAgo === 0 }
  })
}
